import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function PromosTab() {
  const [promos, setPromos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ code: "", discount_type: "percent", discount_value: 10, min_subtotal: 0, max_uses: "", expires_at: "" });

  const load = async () => {
    try {
      const { data } = await api.get("/admin/promo-codes");
      setPromos(data.promos || []);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Could not load");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e?.preventDefault();
    setCreating(true);
    try {
      const payload = {
        ...form,
        discount_value: parseFloat(form.discount_value),
        min_subtotal: parseFloat(form.min_subtotal || 0),
        max_uses: form.max_uses ? parseInt(form.max_uses) : null,
        expires_at: form.expires_at || null,
        code: form.code?.trim() || null,
      };
      const { data } = await api.post("/admin/promo-codes", payload);
      toast.success(`Created ${data.code}`);
      setForm({ code: "", discount_type: "percent", discount_value: 10, min_subtotal: 0, max_uses: "", expires_at: "" });
      load();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Create failed");
    } finally {
      setCreating(false);
    }
  };

  const toggleActive = async (p) => {
    try {
      await api.patch(`/admin/promo-codes/${p.code}`, { active: !p.active });
      load();
    } catch {
      toast.error("Toggle failed");
    }
  };
  const del = async (p) => {
    if (!window.confirm(`Delete ${p.code}? This won't refund existing uses.`)) return;
    try { await api.delete(`/admin/promo-codes/${p.code}`); toast.success("Deleted"); load(); }
    catch { toast.error("Delete failed"); }
  };

  if (loading) return <Loader2 className="w-6 h-6 animate-spin text-[#059669]" />;

  return (
    <div className="space-y-6" data-testid="promos-tab">
      <div className="bg-white border border-[#E5E5E5] p-5">
        <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-[#525252] mb-1">§ Marketing</div>
        <h2 className="font-display font-black text-2xl">Promo codes</h2>
        <p className="text-xs text-[#525252] mt-1">Generate and manage discount codes. Each redemption is logged automatically.</p>
      </div>

      <form onSubmit={create} className="bg-white border border-[#E5E5E5] p-5 grid md:grid-cols-6 gap-3 items-end" data-testid="promo-create-form">
        <div className="md:col-span-2">
          <label className="block text-[10px] uppercase tracking-[0.2em] font-bold text-[#525252] mb-1">Code (blank = auto-generate)</label>
          <input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} placeholder="e.g. SAVE10" data-testid="promo-code-input" className="w-full px-3 py-2 border border-[#E5E5E5] focus:border-[#0A0A0A] outline-none text-sm mono" />
        </div>
        <div>
          <label className="block text-[10px] uppercase tracking-[0.2em] font-bold text-[#525252] mb-1">Type</label>
          <select value={form.discount_type} onChange={(e) => setForm({ ...form, discount_type: e.target.value })} data-testid="promo-type" className="w-full px-3 py-2 border border-[#E5E5E5] outline-none text-sm">
            <option value="percent">Percent</option>
            <option value="fixed">Fixed KES</option>
          </select>
        </div>
        <div>
          <label className="block text-[10px] uppercase tracking-[0.2em] font-bold text-[#525252] mb-1">Value</label>
          <input type="number" min="0" value={form.discount_value} onChange={(e) => setForm({ ...form, discount_value: e.target.value })} data-testid="promo-value" className="w-full px-3 py-2 border border-[#E5E5E5] outline-none text-sm mono" />
        </div>
        <div>
          <label className="block text-[10px] uppercase tracking-[0.2em] font-bold text-[#525252] mb-1">Min order</label>
          <input type="number" min="0" value={form.min_subtotal} onChange={(e) => setForm({ ...form, min_subtotal: e.target.value })} data-testid="promo-min" className="w-full px-3 py-2 border border-[#E5E5E5] outline-none text-sm mono" />
        </div>
        <div>
          <label className="block text-[10px] uppercase tracking-[0.2em] font-bold text-[#525252] mb-1">Max uses (blank = ∞)</label>
          <input type="number" min="1" value={form.max_uses} onChange={(e) => setForm({ ...form, max_uses: e.target.value })} data-testid="promo-max-uses" className="w-full px-3 py-2 border border-[#E5E5E5] outline-none text-sm mono" />
        </div>
        <div className="md:col-span-5">
          <label className="block text-[10px] uppercase tracking-[0.2em] font-bold text-[#525252] mb-1">Expires (blank = never)</label>
          <input type="datetime-local" value={form.expires_at} onChange={(e) => setForm({ ...form, expires_at: e.target.value })} data-testid="promo-expiry" className="w-full px-3 py-2 border border-[#E5E5E5] outline-none text-sm mono" />
        </div>
        <button type="submit" disabled={creating} data-testid="promo-create-btn" className="px-4 py-2 bg-[#059669] text-white text-[10px] uppercase tracking-[0.2em] font-bold hover:bg-[#047857] disabled:opacity-50 inline-flex items-center justify-center gap-2">
          {creating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null} Create
        </button>
      </form>

      <div className="bg-white border border-[#E5E5E5] overflow-x-auto" data-testid="promos-table">
        <table className="w-full text-sm">
          <thead className="bg-[#0A0A0A] text-white">
            <tr>
              <th className="text-left p-3 uppercase tracking-[0.15em] text-[10px]">Code</th>
              <th className="text-left p-3 uppercase tracking-[0.15em] text-[10px]">Discount</th>
              <th className="text-left p-3 uppercase tracking-[0.15em] text-[10px]">Min order</th>
              <th className="text-left p-3 uppercase tracking-[0.15em] text-[10px]">Used / Max</th>
              <th className="text-left p-3 uppercase tracking-[0.15em] text-[10px]">Expires</th>
              <th className="text-left p-3 uppercase tracking-[0.15em] text-[10px]">Status</th>
              <th className="text-left p-3 uppercase tracking-[0.15em] text-[10px]"></th>
            </tr>
          </thead>
          <tbody>
            {promos.length === 0 && <tr><td colSpan={7} className="p-6 text-center text-xs text-[#525252]">No promo codes yet. Create your first one above.</td></tr>}
            {promos.map((p) => (
              <tr key={p.code} className="border-t border-[#E5E5E5]" data-testid={`promo-row-${p.code}`}>
                <td className="p-3 mono font-bold">{p.code}</td>
                <td className="p-3">{p.discount_type === "percent" ? `${p.discount_value}%` : `KES ${p.discount_value}`}</td>
                <td className="p-3 mono">{p.min_subtotal ? `KES ${p.min_subtotal}` : "—"}</td>
                <td className="p-3 mono">{p.used_count}{p.max_uses ? ` / ${p.max_uses}` : ""}</td>
                <td className="p-3 mono text-xs">{p.expires_at ? new Date(p.expires_at).toLocaleString() : "Never"}</td>
                <td className="p-3">
                  <button onClick={() => toggleActive(p)} data-testid={`promo-toggle-${p.code}`} className={`px-2 py-0.5 border text-[10px] uppercase tracking-[0.2em] font-bold ${p.active ? "border-[#059669] bg-[#059669]/10 text-[#059669]" : "border-[#525252] text-[#525252]"}`}>
                    {p.active ? "Active" : "Inactive"}
                  </button>
                </td>
                <td className="p-3 text-right">
                  <button onClick={() => del(p)} data-testid={`promo-delete-${p.code}`} className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#DC2626] hover:underline">Delete</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
